import classNames from 'classnames'
import React, { useId, useState } from 'react'

import { Icon } from '../icon'

import styles from './checkboxV2.module.scss'
import CheckIcon from './icons/check.inline.svg'

interface CheckboxV2Props {
  isChecked?: boolean
  // Both callbacks are supported for different usage patterns:
  // - setIsChecked: For React's useState pattern
  // - onChange: For traditional event handling with additional value information
  setIsChecked?: (checked: boolean) => void
  onChange?: (checked: boolean, value?: string) => void
  label?: string
  className?: string
  labelClassName?: string
  disabled?: boolean
  id?: string
  name?: string
  value?: string
  checkboxClassName?: string
  defaultChecked?: boolean
  ariaLabel?: string
  ariaDescribedby?: string
  onMouseEnter?: React.MouseEventHandler<HTMLLabelElement>
  onMouseLeave?: React.MouseEventHandler<HTMLLabelElement>
}

export const CheckboxV2 = ({
  // Core props
  label,
  isChecked,
  setIsChecked,
  onChange,
  className,
  labelClassName,
  disabled = false,
  id,
  name,
  value,
  checkboxClassName,
  defaultChecked,
  ariaLabel,
  ariaDescribedby,
  onMouseEnter,
  onMouseLeave,
}: CheckboxV2Props) => {
  const generatedId = useId()
  const checkboxId = id ?? `checkbox-${generatedId}`
  const [isHovered, setIsHovered] = useState(false)
  const [localCheckedState, setLocalCheckedState] = useState(defaultChecked ?? false)
  const isControlled = isChecked !== undefined
  const checked = isControlled ? isChecked : localCheckedState

  const handleChange = () => {
    if (disabled) return
    const newChecked = !checked
    if (!isControlled) {
      setLocalCheckedState(newChecked)
    }
    setIsChecked?.(newChecked)
    onChange?.(newChecked, value)
  }

  const wrapperClasses = classNames(styles.checkboxWrapper, className, {
    [styles.disabled as string]: disabled,
    [styles.hovered as string]: isHovered && !disabled,
  })

  const controlClasses = classNames(styles.checkboxControl, checkboxClassName, {
    [styles.checked as string]: checked,
    [styles.hovered as string]: isHovered && !disabled,
  })

  const checkmarkClasses = classNames(styles.checkmark, {
    [styles.checked as string]: checked,
    [styles.hovered as string]: isHovered && !disabled,
  })

  return (
    <label
      htmlFor={checkboxId}
      className={wrapperClasses}
      onMouseEnter={e => {
        setIsHovered(true)
        onMouseEnter?.(e)
      }}
      onMouseLeave={e => {
        setIsHovered(false)
        onMouseLeave?.(e)
      }}
    >
      <div className={styles.checkboxContainer}>
        <input
          id={checkboxId}
          type="checkbox"
          name={name}
          value={value}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className={styles.checkboxInput}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedby}
        />
        <div className={controlClasses}>{checked && <Icon iconSvg={CheckIcon} className={checkmarkClasses} />}</div>
      </div>

      {label && <span className={classNames(styles.labelText, labelClassName)}>{label}</span>}
    </label>
  )
}
